import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  X, User, Mail, Loader2, CheckCircle, 
  AlertTriangle, ExternalLink, Zap, Shield, Check,
  Monitor, Minus, Plus
} from 'lucide-react';
import { CountryPhoneInput, COUNTRIES } from './CountryPhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Risk disclaimers for ORB Algo
const DISCLAIMERS = [
  "I understand that trading involves substantial risk of loss.",
  "Past performance does not guarantee future results.",
  "I am trading with capital I can afford to lose.",
  "I understand the algorithm may have losing periods.",
  "I have read and understood the risk disclosure.",
  "I will not hold Monk Algo liable for any trading losses.",
  "I understand this is a tool, not financial advice.",
  "I am of legal age to trade in my jurisdiction.",
  "I understand subscription fees are non-refundable.",
  "I agree to the Terms of Service and Privacy Policy."
];

// Pricing configuration (matches backend - STANDARD pricing for new members)
const PRICING = {
  quarterly: {
    basePrice: 4999700, // paise - ₹49,997
    label: "Quarterly",
    description: "3 months"
  },
  yearly: {
    basePrice: 14999700, // paise - ₹1,49,997
    label: "Yearly", 
    description: "12 months",
    anchorPrice: 19998800, // ₹1,99,988 (4 × quarterly)
    savings: "Save ₹50,000 (25%)"
  }
};

// Permanent rule (mirrors backend): licenses beyond the first 2 get this % off the regular add-on price.
// Live prices come from /api/orb-algo/razorpay/pricing; PRICING above is only the fallback.
export const ADDON_DISCOUNT_PERCENT = 50;

const formatINR = (paise) => {
  const digits = paise % 100 === 0 ? 0 : 2;
  return `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

// Platform icons
const PlatformIcon = ({ platform }) => {
  if (platform === 'mt5') {
    return (
      <div className="w-8 h-8 rounded-lg bg-[#0EA5E9]/20 flex items-center justify-center">
        <span className="text-[#0EA5E9] font-bold text-xs">MT5</span>
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-lg bg-[#8B5CF6]/20 flex items-center justify-center">
      <span className="text-[#8B5CF6] font-bold text-[10px]">NT8</span>
    </div>
  );
};

export const OrbAlgoSubscribeModal = ({ isOpen, onClose, selectedPlan = 'yearly' }) => {
  const [step, setStep] = useState(1); // 1: Form, 2: Disclaimers, 3: Platform Selection, 4: Payment/Success
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  
  // Country code state for phone
  const [countryCode, setCountryCode] = useState('IN');
  
  // Platform selection state
  const [billingCycle, setBillingCycle] = useState(selectedPlan);
  const [totalQuantity, setTotalQuantity] = useState(2);
  const [mt5Quantity, setMt5Quantity] = useState(2);
  const [ninjatraderQuantity, setNinjatraderQuantity] = useState(0);
  
  const [acceptedDisclaimers, setAcceptedDisclaimers] = useState(
    new Array(DISCLAIMERS.length).fill(false)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [livePricing, setLivePricing] = useState({});

  // Current prices from the server (same source the checkout amount uses)
  useEffect(() => {
    if (!isOpen) return;
    fetch(`${API_URL}/api/orb-algo/razorpay/pricing`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!data?.pricing_options) return;
        const byCycle = {};
        data.pricing_options.forEach(o => { byCycle[o.billing_cycle] = o; });
        setLivePricing(byCycle);
      })
      .catch(() => {});
  }, [isOpen]);

  // Calculate price: base plan covers the first 2 licenses; each extra license is discounted
  const calculatePrice = useCallback(() => {
    const live = livePricing[billingCycle];
    const basePrice = live?.base_price ?? PRICING[billingCycle].basePrice;
    const baseQuantity = live?.base_quantity ?? 2;
    const discountPercent = live?.addon_discount_percent ?? ADDON_DISCOUNT_PERCENT;
    const exactRegular = basePrice / baseQuantity;
    const toWholeRupee = (paise) => Math.floor((paise + 50) / 100) * 100;
    const regularAddonPrice = live?.regular_addon_price ?? toWholeRupee(exactRegular);
    const discountedAddonPrice = live?.discounted_addon_price ?? toWholeRupee(exactRegular * (100 - discountPercent) / 100);
    const addonQuantity = Math.max(0, totalQuantity - baseQuantity);
    const totalPrice = basePrice + addonQuantity * discountedAddonPrice;
    const regularTotal = basePrice + addonQuantity * regularAddonPrice;
    return {
      basePrice,
      baseQuantity,
      discountPercent,
      regularAddonPrice,
      discountedAddonPrice,
      addonQuantity,
      totalPrice,
      savings: regularTotal - totalPrice,
      displayPrice: formatINR(totalPrice),
      displayBasePrice: formatINR(basePrice),
      displayRegularAddon: formatINR(regularAddonPrice),
      displayDiscountedAddon: formatINR(discountedAddonPrice),
      displayAddonTotal: formatINR(addonQuantity * discountedAddonPrice),
      displaySavings: formatINR(regularTotal - totalPrice),
    };
  }, [billingCycle, totalQuantity, livePricing]);

  const pricing = calculatePrice();

  // Update MT5/NT8 split when total changes
  useEffect(() => {
    if (mt5Quantity + ninjatraderQuantity !== totalQuantity) {
      const newMt5 = Math.min(mt5Quantity, totalQuantity);
      setMt5Quantity(newMt5);
      setNinjatraderQuantity(totalQuantity - newMt5);
    }
  }, [totalQuantity, mt5Quantity, ninjatraderQuantity]);

  // Auto-detect country from timezone/IP
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Try timezone-based detection first
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezoneMap = {
          'Asia/Kolkata': 'IN', 'Asia/Karachi': 'PK', 'Asia/Dhaka': 'BD',
          'Asia/Colombo': 'LK', 'Asia/Kathmandu': 'NP', 'Asia/Dubai': 'AE',
          'Asia/Riyadh': 'SA', 'Asia/Singapore': 'SG', 'Asia/Kuala_Lumpur': 'MY',
          'Asia/Jakarta': 'ID', 'Asia/Bangkok': 'TH', 'Asia/Ho_Chi_Minh': 'VN',
          'Asia/Manila': 'PH', 'Asia/Shanghai': 'CN', 'Asia/Tokyo': 'JP',
          'Asia/Seoul': 'KR', 'Asia/Hong_Kong': 'HK', 'America/New_York': 'US',
          'America/Los_Angeles': 'US', 'America/Chicago': 'US', 'America/Toronto': 'CA',
          'Europe/London': 'GB', 'Europe/Berlin': 'DE', 'Europe/Paris': 'FR',
          'Europe/Rome': 'IT', 'Europe/Madrid': 'ES', 'Australia/Sydney': 'AU',
          'Pacific/Auckland': 'NZ', 'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG'
        };
        const detected = timezoneMap[timezone];
        if (detected && COUNTRIES.find(c => c.code === detected)) {
          setCountryCode(detected);
          return;
        }
        // Fallback to IP-based detection
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const data = await res.json();
          const ipCountry = data.country_code?.toUpperCase();
          if (ipCountry && COUNTRIES.find(c => c.code === ipCountry)) {
            setCountryCode(ipCountry);
          }
        }
      } catch (e) {
        // Silent fail - keep default IN
      }
    };
    detectCountry();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const toggleDisclaimer = (index) => {
    setAcceptedDisclaimers(prev => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const selectAllDisclaimers = () => {
    const allSelected = acceptedDisclaimers.every(d => d);
    setAcceptedDisclaimers(new Array(DISCLAIMERS.length).fill(!allSelected));
  };

  const allDisclaimersAccepted = acceptedDisclaimers.every(d => d);

  const validateForm = () => {
    if (!formData.first_name.trim()) return 'First name is required';
    if (!formData.last_name.trim()) return 'Last name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Invalid email format';
    if (!formData.phone.trim()) return 'Phone number is required';
    return null;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setStep(2);
  };

  const handleDisclaimerSubmit = () => {
    if (!allDisclaimersAccepted) {
      setError('Please accept all disclaimers to continue');
      return;
    }
    setStep(3);
  };

  const adjustPlatformQuantity = (platform, delta) => {
    if (platform === 'mt5') {
      const newMt5 = Math.max(0, Math.min(totalQuantity, mt5Quantity + delta));
      setMt5Quantity(newMt5);
      setNinjatraderQuantity(totalQuantity - newMt5);
    } else {
      const newNt = Math.max(0, Math.min(totalQuantity, ninjatraderQuantity + delta));
      setNinjatraderQuantity(newNt);
      setMt5Quantity(totalQuantity - newNt);
    }
  };

  const handlePlatformSubmit = async () => {
    if (mt5Quantity + ninjatraderQuantity !== totalQuantity) {
      setError('Platform quantities must equal total activations');
      return;
    }
    
    // Create subscription directly
    setLoading(true);
    setError('');

    try {
      const selectedCountry = COUNTRIES.find(c => c.code === countryCode) || COUNTRIES[0];
      const formattedPhone = `${selectedCountry.dial}${formData.phone.replace(/\s/g, '')}`;
      
      const response = await fetch(`${API_URL}/api/orb-algo/razorpay/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            phone: formattedPhone,
            country_code: countryCode
          },
          billing_cycle: billingCycle,
          total_quantity: totalQuantity,
          mt5_quantity: mt5Quantity,
          ninjatrader_quantity: ninjatraderQuantity,
          disclaimers_accepted: true,
          disclaimers_accepted_at: new Date().toISOString(),
          accepted_disclaimers: DISCLAIMERS
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubscriptionData(data);
        // Open Razorpay subscription payment page
        if (data.short_url) {
          window.open(data.short_url, '_blank');
        }
        setStep(4); // Show success/instructions
      } else {
        setError(data.detail || 'Failed to create subscription');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({ first_name: '', last_name: '', email: '', phone: '' });
    setCountryCode('IN');
    setAcceptedDisclaimers(new Array(DISCLAIMERS.length).fill(false));
    setBillingCycle(selectedPlan);
    setTotalQuantity(2);
    setMt5Quantity(2);
    setNinjatraderQuantity(0);
    setError('');
    setSubscriptionData(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-lg bg-[#0a0a0f] rounded-2xl border border-white/10 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 bg-gradient-to-r from-[#F59E0B]/10 to-[#10B981]/10 flex-shrink-0">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            data-testid="orb-modal-close-btn"
          >
            <X size={16} className="text-white/60" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F59E0B]/20 to-[#10B981]/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-white">Monk Algo ORB</h2>
              <p className="font-body text-sm text-white/50">
                {step === 1 && 'Enter your details'}
                {step === 2 && 'Review risk disclaimers'}
                {step === 3 && 'Select platforms & quantity'}
                {step === 4 && 'Complete payment'}
              </p>
            </div>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                  s < step ? 'bg-[#10B981] text-white' :
                  s === step ? 'bg-[#F59E0B] text-white' :
                  step === 4 ? 'bg-[#10B981] text-white' :
                  'bg-white/10 text-white/40'
                }`}>
                  {s < step || step === 4 ? <Check size={12} /> : s}
                </div>
                {s < 3 && <div className={`w-4 h-0.5 ${s < step || step === 4 ? 'bg-[#10B981]' : 'bg-white/10'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Step 1: Form */}
          {step === 1 && (
            <form onSubmit={handleFormSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-body text-xs text-white/50 mb-1.5 block">First Name *</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#F59E0B]/50"
                      placeholder="John"
                      data-testid="orb-subscribe-first-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-body text-xs text-white/50 mb-1.5 block">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#F59E0B]/50"
                    placeholder="Doe"
                    data-testid="orb-subscribe-last-name"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-xs text-white/50 mb-1.5 block">Email *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#F59E0B]/50"
                    placeholder="john@example.com"
                    data-testid="orb-subscribe-email"
                  />
                </div>
              </div>

              <div>
                <CountryPhoneInput
                  countryCode={countryCode}
                  setCountryCode={setCountryCode}
                  phone={formData.phone}
                  setPhone={(value) => setFormData(prev => ({ ...prev, phone: value }))}
                  label="Phone"
                  testIdPrefix="orb-subscribe"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle size={16} className="text-red-400" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3.5 bg-gradient-to-r from-[#F59E0B] to-[#10B981] rounded-xl text-white font-heading font-semibold text-sm transition-all hover:opacity-90"
                data-testid="orb-subscribe-continue-btn"
              >
                Continue to Risk Disclosure
              </button>
            </form>
          )}

          {/* Step 2: Disclaimers */}
          {step === 2 && (
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#F59E0B]" />
                  <span className="font-heading font-semibold text-white text-sm">Risk Acknowledgment</span>
                </div>
                <button
                  onClick={selectAllDisclaimers}
                  className="text-xs text-[#F59E0B] hover:text-[#FBBF24] transition-colors"
                >
                  {allDisclaimersAccepted ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                {DISCLAIMERS.map((disclaimer, index) => (
                  <label
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      acceptedDisclaimers[index] 
                        ? 'bg-[#10B981]/10 border border-[#10B981]/30' 
                        : 'bg-white/3 border border-white/5 hover:border-white/10'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={acceptedDisclaimers[index]}
                      onChange={() => toggleDisclaimer(index)}
                      className="mt-0.5 w-4 h-4 rounded border-white/30 bg-white/5 text-[#10B981] focus:ring-[#10B981] focus:ring-offset-0"
                    />
                    <span className="font-body text-xs text-white/70 leading-relaxed">{disclaimer}</span>
                  </label>
                ))}
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle size={16} className="text-red-400" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white font-medium text-sm transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleDisclaimerSubmit}
                  disabled={!allDisclaimersAccepted}
                  className="flex-1 py-3 bg-gradient-to-r from-[#F59E0B] to-[#10B981] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="orb-subscribe-accept-btn"
                >
                  <Check size={18} />
                  I Accept & Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Platform & Quantity Selection */}
          {step === 3 && (
            <div className="p-6 space-y-5">
              {/* Billing Cycle Selection */}
              <div>
                <label className="font-body text-xs text-white/50 mb-2 block">Billing Cycle</label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(PRICING).map(([cycle, config]) => (
                    <button
                      key={cycle}
                      onClick={() => setBillingCycle(cycle)}
                      className={`p-4 rounded-xl border transition-all text-left ${
                        billingCycle === cycle
                          ? 'bg-[#F59E0B]/10 border-[#F59E0B]/50'
                          : 'bg-white/3 border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`billing-${cycle}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-heading font-semibold text-white">{config.label}</span>
                        {config.savings && (
                          <span className="text-[10px] px-2 py-0.5 bg-[#10B981]/20 text-[#10B981] rounded-full">
                            {config.savings}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-white/50">{config.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Add-on discount banner */}
              <div className="flex items-center justify-center gap-2 p-2.5 rounded-xl bg-[#10B981]/10 border border-[#10B981]/30" data-testid="addon-discount-banner">
                <span className="px-2 py-0.5 rounded-full bg-[#10B981] text-white text-[10px] font-bold">{pricing.discountPercent}% OFF</span>
                <span className="text-[#10B981] text-sm font-semibold">All Additional Licenses: {pricing.discountPercent}% OFF</span>
              </div>

              {/* Total Quantity */}
              <div>
                <label className="font-body text-xs text-white/50 mb-2 block">Total Platform Activations</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setTotalQuantity(Math.max(2, totalQuantity - 1))}
                    disabled={totalQuantity <= 2}
                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="quantity-decrease"
                  >
                    <Minus size={18} />
                  </button>
                  <div className="flex-1 text-center">
                    <span className="font-heading font-bold text-3xl text-white">{totalQuantity}</span>
                    <span className="text-white/50 text-sm ml-2">activations</span>
                  </div>
                  <button
                    onClick={() => setTotalQuantity(Math.min(20, totalQuantity + 1))}
                    disabled={totalQuantity >= 20}
                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="quantity-increase"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                <p className="text-center text-white/40 text-xs mt-2" data-testid="addon-price-note">
                  First {pricing.baseQuantity} included in the base plan · each extra license{' '}
                  <span className="line-through text-white/30">{pricing.displayRegularAddon}</span>{' '}
                  <span className="text-[#10B981] font-semibold">{pricing.displayDiscountedAddon}</span>
                </p>
              </div>

              {/* Platform Distribution */}
              <div>
                <label className="font-body text-xs text-white/50 mb-2 block">Platform Distribution</label>
                <div className="space-y-3">
                  {/* MT5 */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/3 border border-white/10">
                    <PlatformIcon platform="mt5" />
                    <div className="flex-1">
                      <span className="font-heading font-medium text-white text-sm">MetaTrader 5</span>
                      <p className="text-xs text-white/40">Futures & Forex</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => adjustPlatformQuantity('mt5', -1)}
                        disabled={mt5Quantity <= 0}
                        className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid="mt5-decrease"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center font-heading font-bold text-white">{mt5Quantity}</span>
                      <button
                        onClick={() => adjustPlatformQuantity('mt5', 1)}
                        disabled={mt5Quantity >= totalQuantity}
                        className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid="mt5-increase"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>

                  {/* NinjaTrader */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/3 border border-white/10">
                    <PlatformIcon platform="ninjatrader" />
                    <div className="flex-1">
                      <span className="font-heading font-medium text-white text-sm">NinjaTrader 8</span>
                      <p className="text-xs text-white/40">Futures Trading</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => adjustPlatformQuantity('ninjatrader', -1)}
                        disabled={ninjatraderQuantity <= 0}
                        className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid="nt8-decrease"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center font-heading font-bold text-white">{ninjatraderQuantity}</span>
                      <button
                        onClick={() => adjustPlatformQuantity('ninjatrader', 1)}
                        disabled={ninjatraderQuantity >= totalQuantity}
                        className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid="nt8-increase"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Preview */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2 text-sm" data-testid="price-preview">
                <div className="flex items-center justify-between">
                  <span className="text-white/60">Base plan ({pricing.baseQuantity} licenses)</span>
                  <span className="text-white" data-testid="price-base">{pricing.displayBasePrice}</span>
                </div>
                {pricing.addonQuantity > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">
                        Additional licenses ({pricing.addonQuantity} × <span className="line-through text-white/30">{pricing.displayRegularAddon}</span>{' '}
                        <span className="text-[#10B981]">{pricing.displayDiscountedAddon}</span>)
                      </span>
                      <span className="text-white" data-testid="price-addons">{pricing.displayAddonTotal}</span>
                    </div>
                    <div className="flex items-center justify-between text-[#10B981]">
                      <span>You save ({pricing.discountPercent}% OFF add-ons)</span>
                      <span className="font-semibold" data-testid="price-savings">−{pricing.displaySavings}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between border-t border-white/10 pt-2">
                  <span className="text-white/60">Total · {PRICING[billingCycle].label.toLowerCase()}</span>
                  <span className="font-heading font-bold text-xl text-white" data-testid="price-total">{pricing.displayPrice}</span>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertTriangle size={16} className="text-red-400" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white font-medium text-sm transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handlePlatformSubmit}
                  disabled={mt5Quantity + ninjatraderQuantity !== totalQuantity}
                  className="flex-1 py-3 bg-gradient-to-r from-[#F59E0B] to-[#10B981] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="orb-subscribe-platform-btn"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Coupon Code */}
          {/* Step 4: Subscription Created - Complete Payment */}
          {step === 4 && (
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#10B981]" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white mb-2">Subscription Created!</h3>
              <p className="font-body text-sm text-white/50 mb-6">
                Complete your payment on the Razorpay page that opened. Once payment is confirmed, your license will be activated automatically.
              </p>
              
              {/* Order Summary */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-left mb-6">
                <h4 className="font-heading font-semibold text-white text-sm mb-3">Subscription Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Plan</span>
                    <span className="text-white">{billingCycle === 'yearly' ? 'Yearly' : 'Quarterly'} (Recurring)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Activations</span>
                    <span className="text-white">{totalQuantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Platforms</span>
                    <span className="text-white">
                      {mt5Quantity > 0 && `${mt5Quantity} MT5`}
                      {mt5Quantity > 0 && ninjatraderQuantity > 0 && ' + '}
                      {ninjatraderQuantity > 0 && `${ninjatraderQuantity} NT8`}
                    </span>
                  </div>
                  <div className="border-t border-white/10 pt-2 mt-2 flex justify-between">
                    <span className="text-white/50">Amount</span>
                    <span className="font-heading font-bold text-[#F59E0B]" data-testid="subscription-amount">
                      {subscriptionData?.amount ? formatINR(subscriptionData.amount) : pricing.displayPrice}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-[#7c3aed]/10 border border-[#7c3aed]/30 text-left mb-6">
                <h4 className="font-heading font-semibold text-[#7c3aed] text-sm mb-2">What's Next?</h4>
                <ul className="space-y-2 text-sm text-white/70">
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#7c3aed] mt-0.5 flex-shrink-0" />
                    <span>Complete payment on the Razorpay page</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#7c3aed] mt-0.5 flex-shrink-0" />
                    <span>You will receive a confirmation email</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#7c3aed] mt-0.5 flex-shrink-0" />
                    <span>Access your license at monkalgo.app</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={16} className="text-[#7c3aed] mt-0.5 flex-shrink-0" />
                    <span>Subscription renews automatically</span>
                  </li>
                </ul>
              </div>

              {subscriptionData?.short_url && (
                <a
                  href={subscriptionData.short_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 bg-gradient-to-r from-[#7c3aed] to-[#06b6d4] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 mb-3"
                  data-testid="orb-open-payment-btn"
                >
                  <Zap size={18} />
                  Open Payment Page
                  <ExternalLink size={14} />
                </a>
              )}

              <a
                href="https://monkalgo.app"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3.5 bg-gradient-to-r from-[#F59E0B] to-[#10B981] rounded-xl text-white font-heading font-semibold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
              >
                Go to Monk Algo App
                <ExternalLink size={14} />
              </a>

              <button
                onClick={handleClose}
                className="w-full mt-3 py-2.5 text-white/50 hover:text-white text-sm transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
